/**
 * Sandbox service — OutpostForge frontend
 * Hypothetical racks/droplets (the v1 model: Racks → Droplets → Instances)
 * for what-if capacity planning, persisted in localStorage. Includes
 * scenario serialization with validation, aggregate helpers for scenario
 * comparison, and cloning a real asset's configuration into the sandbox.
 */

import { calculateTotals, INSTANCE_CATALOG, type InstanceMap } from 'slotting-core';
import type { AssetSummary } from '../types/outposts';
import { findFamily } from './slotting';

export interface SandboxDroplet {
  id: string;
  family: string;
  instances: InstanceMap;
  /** Source asset ID when imported from connected mode (export filenames). */
  label?: string;
}

export interface SandboxRack {
  id: string;
  name: string;
  /** Catalog generation key: 'gen1' | 'gen2'. */
  generation: string;
  droplets: SandboxDroplet[];
}

export interface SandboxState {
  version: 1;
  racks: SandboxRack[];
}

export interface Scenario {
  id: string;
  name: string;
  createdAt: string;
  state: SandboxState;
}

export const EMPTY_SANDBOX: SandboxState = { version: 1, racks: [] };

const SANDBOX_KEY = 'outpostforge.sandbox';

/** Minimal Storage-like interface so tests can inject a fake. */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function defaultStorage(): KeyValueStorage {
  return globalThis.localStorage;
}

export function newId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

// --- Validation / serialization ---

export type ParsedState = { ok: true; state: SandboxState } | { ok: false; error: string };

function isInstanceMap(value: unknown): value is InstanceMap {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.values(value).every(
    (count) => typeof count === 'number' && Number.isInteger(count) && count >= 0 && count <= 9999,
  );
}

/** Validate an arbitrary payload as a SandboxState. */
export function parseSandboxState(data: unknown): ParsedState {
  const state = data as SandboxState;
  if (state === null || typeof state !== 'object') {
    return { ok: false, error: 'Sandbox state must be an object' };
  }
  if (state.version !== 1) {
    return { ok: false, error: `Unsupported sandbox state version '${String(state.version)}'` };
  }
  if (!Array.isArray(state.racks)) {
    return { ok: false, error: 'Sandbox state must have a racks list' };
  }
  for (const [r, rack] of state.racks.entries()) {
    if (typeof rack?.id !== 'string' || typeof rack.name !== 'string') {
      return { ok: false, error: `racks[${r}] must have id and name` };
    }
    if (typeof rack.generation !== 'string' || !INSTANCE_CATALOG[rack.generation]) {
      return { ok: false, error: `racks[${r}] has unknown generation '${String(rack.generation)}'` };
    }
    if (!Array.isArray(rack.droplets)) {
      return { ok: false, error: `racks[${r}] must have a droplets list` };
    }
    for (const [d, droplet] of rack.droplets.entries()) {
      if (typeof droplet?.id !== 'string' || typeof droplet.family !== 'string') {
        return { ok: false, error: `racks[${r}].droplets[${d}] must have id and family` };
      }
      if (!INSTANCE_CATALOG[rack.generation]?.[droplet.family]) {
        return {
          ok: false,
          error: `racks[${r}].droplets[${d}] family '${droplet.family}' is not in generation '${rack.generation}'`,
        };
      }
      if (!isInstanceMap(droplet.instances)) {
        return { ok: false, error: `racks[${r}].droplets[${d}] has an invalid instances map` };
      }
      if (droplet.label !== undefined && typeof droplet.label !== 'string') {
        return { ok: false, error: `racks[${r}].droplets[${d}] label must be a string` };
      }
    }
  }
  return { ok: true, state };
}

/** Parse a serialized sandbox state (JSON string). */
export function deserializeSandboxState(text: string): ParsedState {
  try {
    return parseSandboxState(JSON.parse(text));
  } catch {
    return { ok: false, error: 'Invalid JSON document' };
  }
}

export function serializeSandboxState(state: SandboxState): string {
  return JSON.stringify(state);
}

// --- Persistence ---

export function loadSandbox(storage: KeyValueStorage = defaultStorage()): SandboxState {
  const raw = storage.getItem(SANDBOX_KEY);
  if (!raw) return EMPTY_SANDBOX;
  const parsed = deserializeSandboxState(raw);
  return parsed.ok ? parsed.state : EMPTY_SANDBOX;
}

export function saveSandbox(
  state: SandboxState,
  storage: KeyValueStorage = defaultStorage(),
): void {
  storage.setItem(SANDBOX_KEY, serializeSandboxState(state));
}

export function clearSandbox(storage: KeyValueStorage = defaultStorage()): void {
  storage.removeItem(SANDBOX_KEY);
}

// --- Aggregates (for scenario comparison) ---

export interface ScenarioTotals {
  racks: number;
  droplets: number;
  vCPUs: number;
  memoryGiB: number;
  gpus: number;
}

export function scenarioTotals(state: SandboxState): ScenarioTotals {
  const totals: ScenarioTotals = { racks: 0, droplets: 0, vCPUs: 0, memoryGiB: 0, gpus: 0 };
  for (const rack of state.racks) {
    totals.racks++;
    for (const droplet of rack.droplets) {
      totals.droplets++;
      const familyData = INSTANCE_CATALOG[rack.generation]?.[droplet.family];
      if (!familyData) continue;
      const dropletTotals = calculateTotals(
        { family: droplet.family, instances: droplet.instances },
        familyData,
      );
      totals.vCPUs += dropletTotals.vCPUs;
      totals.memoryGiB += dropletTotals.memoryGiB;
      totals.gpus += dropletTotals.gpus;
    }
  }
  return totals;
}

/** Aggregate all droplet instances into one map (input for diffInstanceMaps). */
export function aggregateInstances(state: SandboxState): InstanceMap {
  const aggregate: InstanceMap = {};
  for (const rack of state.racks) {
    for (const droplet of rack.droplets) {
      for (const [type, count] of Object.entries(droplet.instances)) {
        if (count <= 0) continue;
        aggregate[type] = (aggregate[type] ?? 0) + count;
      }
    }
  }
  return aggregate;
}

// --- Clone from connected mode ---

/**
 * Clone a real asset's current configuration into the sandbox as a new rack
 * with one droplet. Returns the updated state, or an error when the asset's
 * family cannot be resolved against the catalog.
 */
export function cloneAssetToSandbox(
  state: SandboxState,
  asset: AssetSummary,
  sourceOutpostId: string,
): { ok: true; state: SandboxState } | { ok: false; error: string } {
  const familyName = asset.instanceFamilies[0];
  const found = familyName ? findFamily(familyName) : undefined;
  if (!found) {
    return { ok: false, error: `Cannot resolve the family for asset ${asset.assetId}` };
  }

  const droplet: SandboxDroplet = {
    id: newId('droplet'),
    family: found.familyKey,
    instances: Object.fromEntries(
      asset.instanceTypeCapacities.map((c) => [c.instanceType, c.count]),
    ),
    label: asset.assetId,
  };
  const rack: SandboxRack = {
    id: newId('rack'),
    name: `clone of ${asset.assetId} (${sourceOutpostId})`,
    generation: found.generation,
    droplets: [droplet],
  };

  return { ok: true, state: { ...state, racks: [...state.racks, rack] } };
}

/**
 * Clone a whole rack's current configuration into the sandbox: one new
 * sandbox rack with one droplet per compute asset that has a resolvable
 * family (INSTALLING assets without families are skipped).
 */
export function cloneRackToSandbox(
  state: SandboxState,
  rackId: string,
  assets: AssetSummary[],
  sourceOutpostId: string,
): { ok: true; state: SandboxState } | { ok: false; error: string } {
  const rackAssets = assets.filter((a) => a.rackId === rackId);
  const droplets: SandboxDroplet[] = [];
  let generation: string | undefined;

  for (const asset of rackAssets) {
    const familyName = asset.instanceFamilies[0];
    const found = familyName ? findFamily(familyName) : undefined;
    if (!found) continue;
    generation = found.generation;
    droplets.push({
      id: newId('droplet'),
      family: found.familyKey,
      instances: Object.fromEntries(
        asset.instanceTypeCapacities.map((c) => [c.instanceType, c.count]),
      ),
      label: asset.assetId,
    });
  }

  if (droplets.length === 0 || !generation) {
    return { ok: false, error: `Rack ${rackId} has no assets with a resolvable configuration` };
  }

  const rack: SandboxRack = {
    id: newId('rack'),
    name: `clone of ${rackId} (${sourceOutpostId})`,
    generation,
    droplets,
  };
  return { ok: true, state: { ...state, racks: [...state.racks, rack] } };
}

// --- Compute families & JSON export ---

/**
 * Families that are actual compute droplets (slottable capacity). The
 * catalog also carries networking bare-metal entries that must not be
 * offered as sandbox droplets.
 */
export function computeFamilyKeys(generation: string): string[] {
  return Object.entries(INSTANCE_CATALOG[generation] ?? {})
    .filter(([, familyData]) => familyData.category !== 'Accelerated Networking')
    .map(([family]) => family);
}

/**
 * Droplet configuration as a StartCapacityTask-compatible InstancePools
 * document (the capacity editor's Upload JSON accepts it directly).
 */
export function dropletToInstancePoolsJson(droplet: SandboxDroplet): string {
  const pools = Object.entries(droplet.instances)
    .filter(([, count]) => count > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([InstanceType, Count]) => ({ InstanceType, Count }));
  return JSON.stringify({ InstancePools: pools }, null, 2);
}

/** Export filename: sanitized droplet label plus the creation date. */
export function exportFileName(droplet: SandboxDroplet, rackName: string, now = new Date()): string {
  const base = (droplet.label ?? `${rackName}-${droplet.family}`)
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${base}-${now.toISOString().slice(0, 10)}.json`;
}
